import * as React from "react";
import {
    Autocomplete,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useMutation, useQueryClient} from "react-query";
import {useSnackbar} from "notistack";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {useBookingEngines} from "crqlar-react-common/dist/utils/hooks/bookingEngine/useBookingEngineConfigs";
import {BookingEngineService} from "crqlar-react-common/dist/services/bookingEngine/bookingEngine.service";
import {BookingEngine} from "crqlar-react-common/dist/models/bookingEngine/bookingEngine.model";

interface Props {
    serviceId: string
    titleSection?: React.ReactNode
    disabled?: boolean
}

/**
 * Lists the booking engines a service is assigned to and lets the user
 * search-and-assign or remove the service. Each change is persisted immediately
 * by updating the affected booking engine's `serviceIds`.
 */
const ServiceBookingEnginesTab = ({serviceId, titleSection, disabled}: Props) => {

    const {t} = useLocalize()
    const queryClient = useQueryClient()
    const {enqueueSnackbar} = useSnackbar()
    const bookingEngines = useBookingEngines()
    const [assignDialogOpen, setAssignDialogOpen] = React.useState(false)
    const [selectedEngine, setSelectedEngine] = React.useState<BookingEngine | null>(null)
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
    const [menuEngine, setMenuEngine] = React.useState<BookingEngine | null>(null)
    const menuOpen = Boolean(anchorEl)

    const assignedEngines = bookingEngines.filter(be => be.serviceIds?.includes(serviceId))
    const availableEngines = bookingEngines.filter(be => !be.serviceIds?.includes(serviceId))
    const tableColumns = 'minmax(240px, 1fr) 120px 128px'
    const rightCellSx = {
        minWidth: 0,
        textAlign: 'right',
    }

    const mutation = useMutation(
        ['serviceBookingEngineMut', serviceId],
        async (engine: BookingEngine) => BookingEngineService.update(engine),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('bookingEngines')
                enqueueSnackbar(t('Booking engines updated'), {variant: 'success'})
            },
            onError: () => {
                enqueueSnackbar(t('Could not update booking engine'), {variant: 'error'})
            }
        }
    )

    const assignService = (engine: BookingEngine) => {
        const serviceIds = engine.serviceIds ?? []
        if (serviceIds.includes(serviceId)) return
        mutation.mutate({...engine, serviceIds: [...serviceIds, serviceId]})
    }

    const removeService = (engine: BookingEngine) => {
        mutation.mutate({...engine, serviceIds: (engine.serviceIds ?? []).filter(id => id !== serviceId)})
    }

    const controlsDisabled = disabled || mutation.isLoading

    const handleCloseAssignDialog = () => {
        setAssignDialogOpen(false)
        setSelectedEngine(null)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
        setMenuEngine(null)
    }

    const renderViewButton = (engine: BookingEngine) => (
        <Button
            endIcon={<OpenInNewIcon/>}
            sx={{minWidth: 'fit-content'}}
            onClick={() => {
                if (engine.id) {
                    window.open(`/bookingEngine/configurations/${engine.id}`, '_blank')
                }
            }}
        >
            {t('View')}
        </Button>
    )

    const renderMoreButton = (engine: BookingEngine) => (
        <IconButton
            id={`booking-engine-more-menu-${engine.id}`}
            aria-controls={menuOpen ? 'booking-engine-actions-menu' : undefined}
            aria-haspopup={'true'}
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={(event) => {
                event.stopPropagation()
                setAnchorEl(event.currentTarget)
                setMenuEngine(engine)
            }}
            sx={{
                width: 36.5,
                height: 36.5,
                borderRadius: 1,
            }}
        >
            <MoreVertIcon/>
        </IconButton>
    )

    const renderRowActions = (engine: BookingEngine) => (
        <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} gap={1}>
            {renderViewButton(engine)}
            {!controlsDisabled && renderMoreButton(engine)}
        </Stack>
    )

    const renderTableHeader = () => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: tableColumns,
                columnGap: 2,
                width: '100%',
                alignItems: 'center',
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography variant={'body1'} color={'text.secondary'} noWrap>
                {t('Name')}
            </Typography>
            <Typography variant={'body1'} color={'text.secondary'} sx={rightCellSx} noWrap>
                {t('Services')}
            </Typography>
            <Typography variant={'body1'} color={'text.secondary'} sx={rightCellSx} noWrap>
                {t('Actions')}
            </Typography>
        </Box>
    )

    const renderBookingEngineRow = (engine: BookingEngine) => (
        <Box
            key={engine.id}
            sx={{
                display: 'grid',
                gridTemplateColumns: tableColumns,
                columnGap: 2,
                width: '100%',
                alignItems: 'center',
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.08)',
            }}
        >
            <Typography variant={'body1'} noWrap>
                {engine.name || '-'}
            </Typography>

            <Typography variant={'body1'} color={'text.secondary'} sx={rightCellSx} noWrap>
                {engine.serviceIds?.length ?? 0}
            </Typography>

            {renderRowActions(engine)}
        </Box>
    )

    return (
        <ScreenWrapper>
            <Stack sx={{width: '100%', maxWidth: '900px', gap: 2, alignSelf: 'center'}}>
                {titleSection}

                <Alert severity={'info'} sx={{mb: 3}}>
                    {t('Booking engines using this service')}
                </Alert>

                {assignedEngines.length === 0 ? (
                    <Box
                        sx={{
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 2,
                            py: 3,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant={'body1'} color={'text.secondary'}>
                            {t('This service is not used in any booking engine yet.')}
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            overflowX: 'auto',
                            width: '100%',
                        }}
                    >
                        <Stack sx={{minWidth: 560}}>
                            {renderTableHeader()}
                            {assignedEngines.map(renderBookingEngineRow)}
                        </Stack>
                    </Box>
                )}

                {!disabled && (
                    <Stack direction={'row'} justifyContent={'flex-end'} gap={2} mt={2}>
                        <Button
                            id={'btn-assign-booking-engine'}
                            variant={'contained'}
                            startIcon={<SearchIcon/>}
                            disabled={controlsDisabled || availableEngines.length === 0}
                            onClick={() => {
                                setAssignDialogOpen(true)
                            }}
                        >
                            {t('Assign to booking engine')}
                        </Button>
                    </Stack>
                )}
            </Stack>

            <Dialog
                open={assignDialogOpen}
                onClose={handleCloseAssignDialog}
            >
                <DialogTitle>
                    {t('Assign to booking engine')}
                </DialogTitle>

                <DialogContent sx={{'&&': {pt: 1}}}>
                    <Autocomplete<BookingEngine>
                        id={'assign-booking-engine'}
                        options={availableEngines}
                        getOptionLabel={(option) => option.name ?? ''}
                        value={selectedEngine}
                        disabled={controlsDisabled}
                        noOptionsText={t('No booking engines found')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size={'small'}
                                fullWidth
                                placeholder={t('Search booking engine')}
                            />
                        )}
                        onChange={(_e, value) => setSelectedEngine(value)}
                        sx={{width: 400}}
                    />
                </DialogContent>

                <DialogActions>
                    <Button
                        variant={'contained'}
                        color={'primary'}
                        startIcon={<SaveIcon/>}
                        disabled={!selectedEngine?.id || controlsDisabled}
                        onClick={() => {
                            if (!selectedEngine) return
                            assignService(selectedEngine)
                            handleCloseAssignDialog()
                        }}
                    >
                        {t('Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                id={'booking-engine-actions-menu'}
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                MenuListProps={{
                    'aria-labelledby': 'booking-engine-actions-button',
                }}
            >
                <MenuItem
                    disabled={controlsDisabled}
                    onClick={() => {
                        if (menuEngine) {
                            removeService(menuEngine)
                        }
                        handleCloseMenu()
                    }}
                >
                    {t('Remove from booking engine')}
                </MenuItem>
            </Menu>
        </ScreenWrapper>
    )
}

export default ServiceBookingEnginesTab
